import type { DrawingEntity, FurnitureObject, Project, Vec3 } from '../types';
import { faceBasis } from './faces';
import { lineEndpoints } from './line3d';

export type PlanRect = [number, number, number, number];
const intersects = (a: PlanRect, b: PlanRect) => a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
const bounds = (points: Array<[number, number]>): PlanRect => [Math.min(...points.map((p) => p[0])), Math.min(...points.map((p) => p[1])), Math.max(...points.map((p) => p[0])), Math.max(...points.map((p) => p[1]))];

export function furniturePlanBounds(item: FurnitureObject): PlanRect {
  const cos = Math.cos(item.rotation[1]), sin = Math.sin(item.rotation[1]);
  const corners: Array<[number, number]> = [[-1, -1], [-1, 1], [1, -1], [1, 1]].map(([x, z]) => [item.position[0] + x * item.width / 2 * cos - z * item.depth / 2 * sin, item.position[2] + x * item.width / 2 * sin + z * item.depth / 2 * cos]);
  return bounds(corners);
}

export function furnitureWorldCorners(item: FurnitureObject): Vec3[] {
  const cos = Math.cos(item.rotation[1]), sin = Math.sin(item.rotation[1]);
  return ([-1, 1] as const).flatMap((x) => ([-1, 1] as const).flatMap((z) => ([0, 1] as const).map((y) => [
    item.position[0] + x * item.width / 2 * cos - z * item.depth / 2 * sin,
    item.position[1] + y * item.height,
    item.position[2] + x * item.width / 2 * sin + z * item.depth / 2 * cos,
  ] as Vec3)));
}

export function drawingPlanBounds(drawing: DrawingEntity): PlanRect {
  if (drawing.kind === 'circle') {
    const [center, point] = drawing.points;
    const radius = Math.hypot(point[0] - center[0], point[1] - center[1]);
    return [center[0] - radius, center[1] - radius, center[0] + radius, center[1] + radius];
  }
  return bounds(drawing.points);
}

export function drawingWorldVertices(drawing: DrawingEntity): Vec3[] {
  if (drawing.kind === 'polyline' && drawing.vertices3d) return drawing.vertices3d;
  if (drawing.kind === 'face' && drawing.vertices3d) {
    const normal = faceBasis(drawing.vertices3d)?.normal;
    const depth = drawing.extrusionMm ?? 0;
    return normal && depth ? [...drawing.vertices3d, ...drawing.vertices3d.map(([x, y, z]) => [x + normal.x * depth, y + normal.y * depth, z + normal.z * depth] as Vec3)] : drawing.vertices3d;
  }
  if (drawing.kind === 'line') return lineEndpoints(drawing);
  if (drawing.kind === 'rectangle') {
    const [a, b] = drawing.points;
    return [[a[0], 0, a[1]], [a[0], 0, b[1]], [b[0], 0, a[1]], [b[0], 0, b[1]]];
  }
  if (drawing.kind === 'circle') {
    const [center, edge] = drawing.points;
    const radius = Math.hypot(edge[0] - center[0], edge[1] - center[1]);
    return [[center[0] - radius, 0, center[1]], [center[0] + radius, 0, center[1]], [center[0], 0, center[1] - radius], [center[0], 0, center[1] + radius]];
  }
  return drawing.points.map(([x, z]) => [x, 0, z]);
}

export function selectInPlanRect(project: Project, rect: PlanRect) {
  return {
    furnitureIds: project.furniture.filter((item) => item.visible && intersects(rect, furniturePlanBounds(item))).map((item) => item.id),
    drawingIds: project.drawings.filter((drawing) => intersects(rect, drawingPlanBounds(drawing))).map((drawing) => drawing.id),
  };
}
